/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { get as getUser } from 'js/lib/user';

import { useTracker } from '@coursera/event-pulse/react';

import QuickCoachMessage from 'bundles/admin-dashboard/components/QuickCoachMessage';
import { ChatEmptyState } from 'bundles/ai-coach-platform/components/patterns/chat/common';
import type {
  QuickMessage as QuickMessageType,
  SendMessageFunc,
} from 'bundles/ai-coach-platform/components/patterns/chat/types';
import { firstNameFromFullName } from 'bundles/ai-coach-platform/utils/nameParser';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

import _t from 'i18n!nls/admin-dashboard';

type Props = {
  title?: string;
  subtitle?: string;
  quickMessages?: QuickMessageType[];
  sendMessage: SendMessageFunc;
};

const styles = {
  heading: css`
    margin: 0 0 var(--cds-spacing-300) 0;
  `,
  icon: css`
    margin: var(--cds-spacing-300) 0;
    display: flex;
    justify-content: center;
    align-items: center;
  `,

  title: css`
    color: var(--cds-color-emphasis-primary-background-default);
  `,
  subtitle: css`
    margin: 0;
  `,
  quickMessagesContainer: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);

    .coach-secondary-button {
      max-width: 100%;
    }
  `,
};

export const EmptyCoachState = ({ title, subtitle, quickMessages, sendMessage }: Props) => {
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;

  const { fullName } = getUser();
  const userFirstName = fullName ? firstNameFromFullName(fullName) : null;
  const defaultTitle = userFirstName ? _t('Hi, #{userFirstName}!', { userFirstName }) : _t('Hi, Admin!');
  const defaultSubtitle = _t('How can I help?');

  const displayTitle = title ?? defaultTitle;
  const displaySubtitle = subtitle ?? defaultSubtitle;

  const homeViewQuickMessages = React.useMemo(() => quickMessages || [], [quickMessages]);

  return (
    <div
      className="coach-home-view"
      css={css`
        padding: 0 var(--cds-spacing-300) var(--cds-spacing-300);
        height: auto;
        background-color: var(--cds-color-neutral-background-primary);
        border-radius: 0 16px 16px 16px;
      `}
      aria-live="polite"
    >
      <ChatEmptyState title={displayTitle} subtitle={displaySubtitle} />
      <div css={styles.quickMessagesContainer} role="group" aria-label={_t('Quick start buttons')}>
        {homeViewQuickMessages.map(({ message, action, onClick }) => (
          <QuickCoachMessage
            key={message}
            message={message}
            onClick={() => {
              sendMessage(message, action);
              onClick?.();

              if (trackingContext) {
                // v3 tracking
                track('interact_coach', {
                  coachInteractionType: 'click_action',
                  coach: {
                    mode: trackingContext.mode,
                    ...(trackingContext.location && { location: trackingContext.location }),
                  },
                  coachAction: action,
                });
              }
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default EmptyCoachState;
